import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
  Card,
  CardBody,
  CardHeader,
  CardHeaderActions,
  CardHeaderTitle,
  DeleteButton,
} from 'controlled-components'
import {
  clearBottomSelection,
  selectBottomOperator,
  selectLevel1Label,
  selectLevel2OperatorOptions,
} from 'slices/aiSegmentBuilder/picker'

// ═══════════════════════════════════════════════════════════════════════════
// Styles (module-level for stable references)
// ═══════════════════════════════════════════════════════════════════════════

const cardStyle: React.CSSProperties = {
  marginBottom: '12px',
}

// Default operator options for requiresValues check
const DEFAULT_OPERATOR_OPTIONS = [
  { id: 'any', text: 'any of these', requiresValues: true },
  { id: 'all', text: 'all of these', requiresValues: true },
  { id: 'none', text: 'none of these', requiresValues: true },
  { id: 'empty', text: 'no', requiresValues: false },
  { id: 'skip', text: 'any', requiresValues: false },
]

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

interface PickerContainerProps {
  id: number
  /** Index of this picker within its group (0-based, used for display numbering) */
  index: number
  onRemove?: () => void
  disabled?: boolean
  children: React.ReactNode
}

const PickerContainer = React.memo(
  ({
    id,
    index,
    onRemove,
    disabled = false,
    children,
  }: PickerContainerProps) => {
    const dispatch = useDispatch()

    // Get label from group config (via pickerId mapping)
    const label = useSelector((state: any) => selectLevel1Label(state, id))

    // Build title using label and index (1-based for display)
    const title = `${label.toUpperCase()} ${index + 1}`

    // Check if current operator requires values (to determine if BottomSelector should show)
    const operator = useSelector(
      useCallback((state: any) => selectBottomOperator(state, id), [id])
    )
    const serverOperatorOptions = useSelector((state: any) =>
      selectLevel2OperatorOptions(state, id)
    )
    const operatorOptions =
      serverOperatorOptions.length > 0
        ? serverOperatorOptions
        : DEFAULT_OPERATOR_OPTIONS
    const selectedOperatorOption = operatorOptions.find(
      opt => opt.id === operator
    )
    const requiresValues = selectedOperatorOption?.requiresValues !== false

    // Clear bottom selection when switching to an operator that doesn't require values
    useEffect(() => {
      if (!requiresValues) {
        dispatch(clearBottomSelection({ pickerId: id }))
      }
    }, [requiresValues, dispatch, id])

    // Filter children to exclude BottomSelectorContainer when operator doesn't require values
    // and inject pickerId and disabled props into all children
    const processedChildren = useMemo(() => {
      const childArray = React.Children.toArray(children)

      // Filter out BottomSelectorContainer if operator doesn't require values
      const filtered = requiresValues
        ? childArray
        : childArray.filter(child => {
            if (
              React.isValidElement(child) &&
              typeof child.type !== 'string' &&
              (child.type as any).displayName === 'BottomSelectorContainer'
            ) {
              return false
            }
            return true
          })

      // Inject pickerId and disabled props into all children
      return filtered.map(child =>
        React.isValidElement(child)
          ? React.cloneElement(child as React.ReactElement<any>, {
              pickerId: id,
              disabled,
            })
          : child
      )
    }, [children, requiresValues, id, disabled])

    // Whether delete button should be shown
    const showDelete = Boolean(onRemove) && !disabled

    return (
      <Card variant="outlined" style={cardStyle}>
        <CardHeader>
          <CardHeaderTitle>{title}</CardHeaderTitle>
          {showDelete && (
            <CardHeaderActions>
              <DeleteButton
                onClick={onRemove}
                aria-label={`Delete ${title}`}
                disabled={disabled}
              />
            </CardHeaderActions>
          )}
        </CardHeader>
        <CardBody padding="md">{processedChildren}</CardBody>
      </Card>
    )
  }
)

PickerContainer.displayName = 'PickerContainer'

export default PickerContainer
